/**
 * Ways sign-in (PO-92 F5.1 S5.1.1).
 *
 * Ways (hs-ways-to-work) accepts only hs-auth tokens minted for ITS app, so PMO runs a second
 * PKCE sign-in for app `ways`, next to its own. hs-auth keeps no session of its own: the round
 * trip goes through Google, which passes straight through when the Google session is live (the
 * `login_hint` picks the account). After that the refresh token keeps it alive without a redirect.
 *
 * Two tokens, both kept per browser:
 *  - the hs-auth `ways` token: proves who the person is. Sent as `X-HS-Identity` on writes
 *    that act in PMO (D-6), and used to mint the session.
 *  - the `wtw_` session token: what every Ways REST call carries. It never outlives the sign-in
 *    token, so it is re-minted, under the same session id, when it lapses.
 *
 * Everything is kept apart from PMO's own sign-in: other storage keys, other PKCE state.
 */
import { generateCodeChallenge, generateCodeVerifier, requireGatewayUrl } from './auth';

const APP_ID = import.meta.env.VITE_WAYS_APP_ID || 'ways';
export const WAYS_API_URL: string = (import.meta.env.VITE_WAYS_API_URL || 'https://ways.hsinc.ai').replace(/\/$/, '');
export const WAYS_CALLBACK_PATH = '/auth/ways/callback';

const IDENTITY_KEY = 'hs_ways_identity';
const SESSION_KEY = 'hs_ways_session';
const SESSION_ID_KEY = 'hs_ways_session_id';
const VERIFIER_KEY = 'hs_ways_code_verifier';
const STATE_KEY = 'hs_ways_state';
const RETURN_KEY = 'hs_ways_return_to';
const MODE_KEY = 'hs_ways_mode';
const SILENT_TRIED_KEY = 'hs_ways_silent_tried';

/** What Google answers to prompt=none when it cannot sign in without the person: ask them instead. */
const NEEDS_INTERACTION = new Set([
  'login_required', 'interaction_required', 'consent_required', 'account_selection_required',
]);
const redirectUri = () => `${window.location.origin}${WAYS_CALLBACK_PATH}`;

interface Stored {
  token: string;
  expires_at: number; // epoch ms
}
interface IdentitySet extends Stored {
  refresh_token: string;
}

/** Signed-out (or never signed in): the page offers the sign-in instead of failing. */
export class WaysSignInRequired extends Error {
  constructor(message = 'Sign in to Ways to continue') {
    super(message);
    this.name = 'WaysSignInRequired';
  }
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function fresh(s: Stored | null): string | null {
  return s && s.expires_at - Date.now() > 60_000 ? s.token : null;
}

/** One session id per browser, so a re-mint renews the same Ways session rather than adding one. */
function sessionId(): string {
  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = `pmo-ui-${generateCodeVerifier().slice(0, 16)}`;
    localStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

// ─── Sign-in round trip ─────────────────────────────────────────

/**
 * Start the round trip. `silent` sends prompt=none: with a live Google session it comes straight
 * back with a code and the person sees nothing; without one hs-auth returns `login_required` (or
 * similar) to the callback, which then starts the interactive sign-in. At most one silent try per
 * tab, so a refusal can never loop.
 */
export async function signIn(loginHint?: string, returnTo?: string, silent = false): Promise<void> {
  const verifier = generateCodeVerifier();
  const state = generateCodeVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo || window.location.pathname + window.location.search);
  sessionStorage.setItem(MODE_KEY, silent ? 'silent' : 'interactive');
  if (silent) sessionStorage.setItem(SILENT_TRIED_KEY, '1');

  const params = new URLSearchParams({
    app_id: APP_ID,
    redirect_uri: redirectUri(),
    code_challenge: await generateCodeChallenge(verifier),
    code_challenge_method: 'S256',
    state,
  });
  if (loginHint) params.set('login_hint', loginHint);
  if (silent) params.set('prompt', 'none');
  window.location.href = `${requireGatewayUrl()}/api/auth/authorize?${params.toString()}`;
}

/** Has this tab already tried the silent sign-in? Then the page offers the button instead. */
export function silentTried(): boolean {
  return sessionStorage.getItem(SILENT_TRIED_KEY) === '1';
}

/**
 * Finish the round trip; returns where to go next, or null when it has moved on to the
 * interactive sign-in (a silent try Google could not complete). Throws with a readable message.
 */
export async function completeSignIn(searchParams: URLSearchParams, loginHint?: string): Promise<string | null> {
  const error = searchParams.get('error');
  const state = searchParams.get('state');
  if (!state || state !== sessionStorage.getItem(STATE_KEY)) {
    throw new Error('State mismatch: start the sign-in again');
  }
  if (error) {
    if (NEEDS_INTERACTION.has(error) && sessionStorage.getItem(MODE_KEY) === 'silent') {
      await signIn(loginHint, sessionStorage.getItem(RETURN_KEY) || '/ways');
      return null;
    }
    throw new Error(searchParams.get('error_description') || `Ways sign-in failed: ${error}`);
  }
  const code = searchParams.get('code');
  if (!code) throw new Error('The sign-in response is missing its code');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE code verifier: start the sign-in again');

  // PMO's /api/auth/token proxy forwards to hs-auth, whatever the app.
  const res = await fetch('/api/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // redirect_uri must be the authorize one exactly: hs-auth refuses a mismatch and burns the code.
    body: JSON.stringify({
      grant_type: 'authorization_code', code, code_verifier: verifier, app_id: APP_ID, redirect_uri: redirectUri(),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Ways sign-in failed');
  storeIdentity(data);
  localStorage.removeItem(SESSION_KEY); // a new person or a new sign-in: mint afresh

  const back = sessionStorage.getItem(RETURN_KEY) || '/ways';
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  sessionStorage.removeItem(MODE_KEY);
  return back.startsWith('/') && !back.startsWith('//') ? back : '/ways';
}

function storeIdentity(d: { access_token: string; refresh_token: string; expires_in: number }) {
  const set: IdentitySet = {
    token: d.access_token,
    refresh_token: d.refresh_token,
    expires_at: Date.now() + d.expires_in * 1000,
  };
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(set));
}

export function signOut(): void {
  const id = read<IdentitySet>(IDENTITY_KEY);
  if (id?.refresh_token) {
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: id.refresh_token }),
    }).catch(() => {});
  }
  localStorage.removeItem(IDENTITY_KEY);
  localStorage.removeItem(SESSION_KEY);
}

export function hasSignedIn(): boolean {
  return read<IdentitySet>(IDENTITY_KEY) !== null;
}

// ─── Tokens ─────────────────────────────────────────────────────

let identityInFlight: Promise<string> | null = null;
let sessionInFlight: Promise<string> | null = null;

/** The person's hs-auth `ways` token, refreshed when close to expiry. */
export async function getIdentity(): Promise<string> {
  const id = read<IdentitySet>(IDENTITY_KEY);
  const ok = fresh(id);
  if (ok) return ok;
  if (!id?.refresh_token) throw new WaysSignInRequired();
  identityInFlight ??= (async () => {
    try {
      // The OAuth refresh grant (it rotates the refresh token), through PMO's token proxy.
      const res = await fetch('/api/auth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: id.refresh_token, client_id: APP_ID }),
      });
      const data = await res.json().catch(() => ({}));
      // A refresh token that is spent, expired or revoked is a 400 invalid_grant (RFC 6749 5.2),
      // not a 401: it is final, so sign in again rather than retrying it forever.
      if (res.status === 401 || res.status === 403 || data.error === 'invalid_grant') {
        signOut();
        throw new WaysSignInRequired('Your Ways sign-in has ended: sign in again');
      }
      if (!res.ok) throw new Error('Could not renew the Ways sign-in; try again shortly');
      storeIdentity(data);
      return data.access_token as string;
    } finally {
      identityInFlight = null;
    }
  })();
  return identityInFlight;
}

/** The `wtw_` session token for REST calls, minted (or renewed) when absent or close to expiry. */
export async function getSession(force = false): Promise<string> {
  const ok = force ? null : fresh(read<Stored>(SESSION_KEY));
  if (ok) return ok;
  sessionInFlight ??= (async () => {
    try {
      const jwt = await getIdentity();
      const res = await fetch(`${WAYS_API_URL}/api/sessions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        // No repository: a person in PMO is not bound to one checkout. Ways shows every project
        // the person may read and acts in PMO through the target project's repository.
        body: JSON.stringify({
          sessionId: sessionId(),
          actorClass: 'human',
          client: 'pmo-ui',
          host: window.location.host,
          sessionLabel: 'PMO',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        signOut();
        throw new WaysSignInRequired(data.message);
      }
      if (!res.ok) throw new Error(data.message || data.error || 'Could not open a Ways session');
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ token: data.token, expires_at: Date.now() + data.expiresIn * 1000 }),
      );
      return data.token as string;
    } finally {
      sessionInFlight = null;
    }
  })();
  return sessionInFlight;
}
